// src/config/market.js
//
// Multi-national market registry for the Lusuku customer web client.
//
// One codebase, N markets. The active country is resolved ONCE here, at
// build time, from `process.env.REACT_APP_COUNTRY` (set per Netlify site):
//   lusuku.ug     -> REACT_APP_COUNTRY=UG (default)
//   lusuku.co.zm  -> REACT_APP_COUNTRY=ZM
//
// Country codes are ISO 3166-1 alpha-2. Never store a country as a display
// name, dial code, or currency. Read the registry — do not hardcode "UGX",
// "en-UG", "256", or `country:"ug"` anywhere else.
//
// Framework-free on purpose (no React import) so it can be imported from
// api clients, utils, SEO, and components alike.

const DEFAULT_COUNTRY = "UG";

// The market registry. Add a new market = add an entry here + seed its
// backend configs. That is the scalability contract.
export const MARKETS = {
  UG: {
    country: "UG",
    name: "Uganda",
    nationality: "Ugandan",
    currency: "UGX",
    currencyLocale: "en-UG",
    dialCode: "256",
    cca2: "ug",
    geocodeCountry: "ug",
    /* Local trunk prefixes per mobile-money network, for the "is this an
       MTN number" checks on the checkouts. Mirrors the apps' utils/
       momoNetwork.js table. Regulator-allocated and they change, so treat
       as advisory: an unknown prefix is a warning, never a hard refusal. */
    momoPrefixes: {
      MOMO: ["076", "077", "078", "039"],
      Airtel: ["070", "074", "075", "020"],
    },
    mapCenter: { lat: 0.347596, lng: 32.58252 },
    mapOrigin: { lat: 0.3148136, lng: 32.5788721 },
    // Generous national bounding box (with margin) — used to reject GPS fixes
    // and manual picks that plainly aren't in this market.
    bounds: { minLat: -1.6, maxLat: 4.4, minLng: 29.3, maxLng: 35.2 },
    siteOrigin: "https://lusuku.ug",
    locationPromptExample: "e.g., Kololo, Ntinda, or a hotel name",
    deliveryLocationExample: "e.g. Kampala, Industrial Area",
    primaryCity: "Kampala",
    deliveryZones: [
      "Kampala Central", "Nakawa", "Kawempe", "Rubaga",
      "Makindye", "Ntinda", "Kololo", "Bugolobi",
      "Kisaasi", "Naalya", "Kira", "Najjera",
      "Bukoto", "Wandegeya", "Makerere", "Kyebando",
    ],
    supportPhone: "0323 300 212",
    supportTel: "tel:+256323300212",
    /* The Lusuku WhatsApp Business line (the platform WABA). Confirmed
       receiving messages on 2 Nov 2026; replaced +256 700 771 301. */
    supportWhatsApp: "256785010102",
    supportWhatsAppDisplay: "+256 785 010 102",
    supportEmail: "support@lusuku.ug",
    officeAddress: ["SN Plaza, Ntinda Kigoowa", "Tuund Road, Kampala, Uganda"],
    facebookUrl: "https://www.facebook.com/lusuku.ug",
    flag: "\u{1F1FA}\u{1F1EC}", // UG flag
  },
  ZM: {
    country: "ZM",
    name: "Zambia",
    nationality: "Zambian",
    currency: "ZMW",
    currencyLocale: "en-ZM",
    dialCode: "260",
    cca2: "zm",
    geocodeCountry: "zm",
    momoPrefixes: {
      MOMO: ["096", "076"],
      Airtel: ["097", "077"],
      ZAMTEL: ["095", "075"],
    },
    mapCenter: { lat: -15.3875, lng: 28.3228 },
    mapOrigin: { lat: -15.3875, lng: 28.3228 },
    bounds: { minLat: -18.3, maxLat: -8.0, minLng: 21.7, maxLng: 33.9 },
    siteOrigin: "https://lusuku.co.zm",
    locationPromptExample: "e.g., Lusaka, Kabulonga, or a hotel name",
    deliveryLocationExample: "e.g. Lusaka, Industrial Area",
    primaryCity: "Lusaka",
    deliveryZones: [
      "Lusaka Central", "Kabulonga", "Woodlands", "Roma",
      "Avondale", "Chilenje", "Chelstone", "Longacres",
      "Rhodes Park", "Matero", "Kalingalinga", "Ibex Hill",
    ],
    supportPhone: "",
    supportTel: "",
    supportWhatsApp: "",
    supportWhatsAppDisplay: "",
    supportEmail: "support@lusuku.co.zm",
    officeAddress: ["Lusaka, Zambia"],
    facebookUrl: "https://www.facebook.com/lusuku",
    flag: "\u{1F1FF}\u{1F1F2}", // ZM flag
  },
};

// Preferred country ordering for the phone-input widget, active market first.
const PHONE_PREFERRED_BY_COUNTRY = {
  UG: ["ug", "ke", "tz", "rw"],
  ZM: ["zm"],
};

// ── Customer-facing payment methods per market ───────────────────────────────
// `key` is the value persisted as Order.paymentMethod (backend enum:
// COD/MOMO/Airtel/Visa/CARD/ZAMTEL). `rail` picks the checkout flow:
//   MTN_DIRECT — Uganda's direct MTN MoMo collection (POST /momo/:clientId)
//   FW_MOMO    — Flutterwave mobile money (POST /fw/flutterAirtelPayment with
//                `network`); UG Airtel and ALL Zambian mobile money (the
//                backend charges MobileMoney.zambia in ZMW for ZM requests)
//   CARD       — Flutterwave card
//   COD        — cash on delivery (no charge call)
// Mirrors the server's registry (GET /api/markets); the server remains
// authoritative — it re-validates network-vs-market on every charge.
const LOGOS = {
  mtn: "https://res.cloudinary.com/lusuku/image/upload/v1693301528/Lusuku_Public_Resources/Mtn_Momo_twnyoa.svg",
  airtel:
    "https://res.cloudinary.com/lusuku/image/upload/v1693304179/Lusuku_Public_Resources/airtel_money_lusuku_htjiy7.png",
  visa: "https://res.cloudinary.com/lusuku/image/upload/v1693302551/Lusuku_Public_Resources/Visa_Inc.-Logo.lusuku_xrhhuw.svg",
  cod: "https://res.cloudinary.com/lusuku/image/upload/v1693307048/Lusuku_Public_Resources/cod_lusuku_mygqcm.png",
};

const PAYMENT_METHODS_BY_COUNTRY = {
  UG: [
    { key: "MOMO", label: "MTN Mobile Money", rail: "MTN_DIRECT", imageUrl: LOGOS.mtn },
    { key: "Airtel", label: "Airtel Money", rail: "FW_MOMO", network: "AIRTEL", imageUrl: LOGOS.airtel },
    { key: "Visa", label: "Credit Card", rail: "CARD", imageUrl: LOGOS.visa },
    { key: "COD", label: "Cash On Delivery", rail: "COD", imageUrl: LOGOS.cod },
  ],
  ZM: [
    { key: "MOMO", label: "MTN Mobile Money", rail: "FW_MOMO", network: "MTN", imageUrl: LOGOS.mtn },
    { key: "Airtel", label: "Airtel Money", rail: "FW_MOMO", network: "AIRTEL", imageUrl: LOGOS.airtel },
    // No hosted Zamtel logo yet — screens must render the label when imageUrl is empty.
    { key: "ZAMTEL", label: "Zamtel Kwacha", rail: "FW_MOMO", network: "ZAMTEL", imageUrl: "" },
    { key: "Visa", label: "Credit / Debit Card", rail: "CARD", imageUrl: LOGOS.visa },
    { key: "COD", label: "Cash On Delivery", rail: "COD", imageUrl: LOGOS.cod },
  ],
};

// Fail-safe lookup: unknown/blank codes resolve to Uganda so a
// misconfigured build never crashes and never silently loses currency.
export function getMarket(c) {
  const code = typeof c === "string" ? c.trim().toUpperCase() : "";
  return MARKETS[code] || MARKETS[DEFAULT_COUNTRY];
}

// Resolve the active country from the build-time env var (default UG).
const RESOLVED_COUNTRY = (() => {
  const raw = process.env.REACT_APP_COUNTRY;
  const code = typeof raw === "string" ? raw.trim().toUpperCase() : "";
  return MARKETS[code] ? code : DEFAULT_COUNTRY;
})();

// Active market.
export const COUNTRY = RESOLVED_COUNTRY;
export const MARKET = getMarket(COUNTRY);

// Convenience exports derived from the active market.
export const CURRENCY = MARKET.currency;
export const CURRENCY_LOCALE = MARKET.currencyLocale;
/* The minimum order value is not in this registry on purpose: it is set in the
   dashboard (Settings > Minimum order value) and read live from the server by
   hooks/useMinimumOrderValue, so a change there reaches every cart without a
   web release. */
export const DIAL_CODE = MARKET.dialCode;
export const PHONE_COUNTRY = MARKET.cca2;
export const PHONE_PREFERRED =
  PHONE_PREFERRED_BY_COUNTRY[COUNTRY] || PHONE_PREFERRED_BY_COUNTRY[DEFAULT_COUNTRY];
export const MAPS_COUNTRY = MARKET.geocodeCountry;
export const MAP_CENTER = MARKET.mapCenter;
export const MAP_ORIGIN = MARKET.mapOrigin || MARKET.mapCenter;
export const SITE_ORIGIN = MARKET.siteOrigin;
export const LOCATION_PROMPT_EXAMPLE =
  MARKET.locationPromptExample || `Search an address in ${MARKET.name}`;
export const DELIVERY_LOCATION_EXAMPLE =
  MARKET.deliveryLocationExample || `Search a delivery area in ${MARKET.name}`;
export const SUPPORT_PHONE = MARKET.supportPhone;
export const SUPPORT_TEL = MARKET.supportTel;
export const SUPPORT_WHATSAPP = MARKET.supportWhatsApp;
export const SUPPORT_WHATSAPP_DISPLAY = MARKET.supportWhatsAppDisplay;
export const SUPPORT_EMAIL = MARKET.supportEmail;
export const OFFICE_ADDRESS = MARKET.officeAddress || [];
export const FACEBOOK_URL = MARKET.facebookUrl;

/**
 * TikTok is ONE account for the whole platform, so it is a plain module
 * constant rather than a field on each market.
 *
 * That is a real distinction, not a shortcut. `facebookUrl` sits inside MARKETS
 * because Uganda and Zambia genuinely run separate pages, so the value has to
 * follow the resolved country. TikTok does not: putting it in the registry
 * would mean writing the same handle twice and inviting the two copies to drift
 * apart. If a second TikTok account is ever opened for another market, move
 * this into MARKETS rather than adding a second constant here.
 */
export const TIKTOK_URL = "https://www.tiktok.com/@lusuku_";

/* Instagram and LinkedIn are one account each for the whole platform too (the
   same reasoning as TikTok above). Instagram is @lusuku_marketplace since
   21 Aug 2026; @lusukuonline was never ours. */
export const INSTAGRAM_URL = "https://www.instagram.com/lusuku_marketplace/";
export const LINKEDIN_URL = "https://www.linkedin.com/company/lusuku";

/**
 * Every public profile of this market's Lusuku, in one list. The footer links
 * to them and the homepage's structured data names them (schema.org `sameAs`),
 * which is how Google ties the accounts to the Lusuku it shows for a search.
 * One list, so a new account is added once and appears in both.
 */
export const SOCIAL_PROFILE_URLS = Object.freeze(
  [FACEBOOK_URL, INSTAGRAM_URL, TIKTOK_URL, LINKEDIN_URL].filter(Boolean)
);

export const COUNTRY_NAME = MARKET.name;
export const NATIONALITY = MARKET.nationality || `${MARKET.name}n`;
export const PRIMARY_CITY = MARKET.primaryCity || MARKET.name;
export const DELIVERY_ZONES = MARKET.deliveryZones || [];
export const MARKET_BOUNDS = MARKET.bounds || null;
/* Local trunk prefixes for the active market's MTN MoMo network (see
   momoPrefixes above). Empty when the market has not declared any, in which
   case callers must fall back to a plain length check, never to Uganda's. */
export const MTN_PREFIXES = (MARKET.momoPrefixes && MARKET.momoPrefixes.MOMO) || [];

// Payment methods for the ACTIVE market (see PAYMENT_METHODS_BY_COUNTRY above).
export const PAYMENT_METHODS =
  PAYMENT_METHODS_BY_COUNTRY[COUNTRY] || PAYMENT_METHODS_BY_COUNTRY[DEFAULT_COUNTRY];

/** The registry entry for a method key ("MOMO", "Airtel", "ZAMTEL", …). */
export function paymentMethodFor(key) {
  return PAYMENT_METHODS.find((m) => m.key === key) || null;
}

/** True when this method charges over the Flutterwave mobile-money rail. */
export function isFwMomoMethod(key) {
  const m = paymentMethodFor(key);
  return Boolean(m && m.rail === "FW_MOMO");
}

/** The FW `network` a method's charge must carry (MTN/AIRTEL/ZAMTEL), or null. */
export function fwNetworkFor(key) {
  return paymentMethodFor(key)?.network || null;
}

/** Display label for a method key (falls back to the key itself). */
export function methodLabelFor(key) {
  return paymentMethodFor(key)?.label || String(key || "");
}

// Is a coordinate plausibly inside the active market? Used to refuse GPS
// fixes / manual picks that are plainly in another country (a VPN'd browser,
// a mistyped search, a stale device fix). Fails OPEN when the market has no
// bounds configured; fails CLOSED on non-finite coords.
export function isWithinMarketBounds(lat, lng) {
  // Reject null/undefined/'' BEFORE Number() coercion — Number(null) is 0,
  // and (0, 32.58) is a real place in Uganda (the equator crosses it), so
  // coercion would silently accept missing coordinates.
  if (lat == null || lng == null || lat === "" || lng === "") return false;
  const la = Number(lat);
  const ln = Number(lng);
  if (!Number.isFinite(la) || !Number.isFinite(ln)) return false;
  if (!MARKET_BOUNDS) return true;
  return (
    la >= MARKET_BOUNDS.minLat &&
    la <= MARKET_BOUNDS.maxLat &&
    ln >= MARKET_BOUNDS.minLng &&
    ln <= MARKET_BOUNDS.maxLng
  );
}

/**
 * Convert an unknown coordinate pair into the canonical shape used by maps,
 * coverage, shipping, and saved addresses.
 *
 * Returning null instead of a partial/default coordinate is intentional:
 * checkout must never silently turn a missing value into (0, 0).
 */
export function normalizeMarketLatLng(lat, lng) {
  if (!isWithinMarketBounds(lat, lng)) return null;
  return { lat: Number(lat), lng: Number(lng) };
}

// Plain integer formatter for the active market — counts, not money.
// Brand products carry six-figure stock levels and four-figure order minimums,
// and "1000000 available" is not a number anyone can read at a glance. Shares
// CURRENCY_LOCALE with formatCurrency so a count and the price beside it group
// their digits the same way.
export function formatCount(value) {
  const n = Number.isFinite(Number(value)) ? Number(value) : 0;
  try {
    return new Intl.NumberFormat(CURRENCY_LOCALE, {
      maximumFractionDigits: 0,
    }).format(n);
  } catch (_) {
    return String(n);
  }
}

// Currency formatter for the active market.
// - non-finite input coerces to 0 (never renders "NaN"/"undefined")
// - Intl failures degrade gracefully to "<CUR> <n>"
export function formatCurrency(amount) {
  const n = Number.isFinite(Number(amount)) ? Number(amount) : 0;
  try {
    return new Intl.NumberFormat(CURRENCY_LOCALE, {
      style: "currency",
      currency: CURRENCY,
    }).format(n);
  } catch (_) {
    return `${CURRENCY} ${n}`;
  }
}
